import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {CheckoutNavigationService} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/CheckoutNavigationService/CheckoutNavigationService';
import {CheckoutApiService} from './checkout-api-service';
import {
  IProductDTO,
  IPropsInjectedByViewerScript,
  StandaloneCheckoutIds,
  TranslationDictionary,
  UserInput,
  VolatileCartFromServer,
} from '../types/app-types';
import {CheckoutLegacyMutationsCreateCartArgs} from '@wix/wixstores-graphql-schema-node';
import {ProductService} from './ProductService';
import {StoreMetaDataService} from './StoreMetaDataService';
import {ModalType} from '../constants';
import {SPECS} from '../specs';
import {ReadOnlyExperiments} from '@wix/wix-experiments';
import {ControllerFlowAPI} from '@wix/yoshi-flow-editor';

export const VolatileCartApiUrl = '/stores/v1/carts/volatileCart';

export class VolatileCartService {
  private volatileCart: VolatileCartFromServer['cart'] & {checkoutId?: string};
  private readonly checkoutApiService: CheckoutApiService;
  private readonly checkoutNavigationService: CheckoutNavigationService;

  constructor(
    private readonly siteStore: SiteStore,
    private readonly storeMetaDataService: StoreMetaDataService,
    private readonly reportError: (e) => any,
    private readonly errorHandler: ControllerFlowAPI['errorHandler'],
    private readonly wixCodeApi: any,
    private readonly experiments: ReadOnlyExperiments,
    private readonly nextProps: (additionalProps: Partial<IPropsInjectedByViewerScript>) => void,
    private readonly translations: TranslationDictionary
  ) {
    this.checkoutApiService = new CheckoutApiService(wixCodeApi, errorHandler, translations);
    this.checkoutNavigationService = new CheckoutNavigationService({siteStore, origin: 'product-page'});
  }

  public getStandaloneCheckoutIds = async (
    product: IProductDTO,
    userInputs: UserInput,
    isPreOrderState: boolean
  ): Promise<StandaloneCheckoutIds> => {
    const customTextFieldSelection = ProductService.mapCustomTextFields(product, userInputs);
    const isFixVariantIdCalculationInBuyNowFlow = this.experiments.enabled(SPECS.FixVariantIdCalculationInBuyNowFlow);
    const params: CheckoutLegacyMutationsCreateCartArgs = {
      productId: ProductService.getProductId(product),
      optionSelectionId: ProductService.mapOptionSelectionIds(userInputs),
      customTextFieldSelection,
      quantity: ProductService.getQuantity(userInputs),
      subscriptionOptionId: ProductService.getSubscriptionOptionId(userInputs),
      variantId: ProductService.getSelectedVariantId(product, userInputs, isFixVariantIdCalculationInBuyNowFlow),
      options: ProductService.mapUserInputsToSelectedOptions(product, userInputs),
    };

    if (isPreOrderState) {
      params.preOrderRequested = true;
    }

    const isUseCartV2ForDirectPurchase = this.experiments.enabled(SPECS.UseCartV2ForDirectPurchase);
    if (!isUseCartV2ForDirectPurchase) {
      /* istanbul ignore next: todo: test */
      await this.checkoutApiService
        .legacyCreateCheckout(params)
        .then((res) => {
          this.volatileCart = res;
        })
        .catch(() => {
          void this.checkoutNavigationService.openModalByType(ModalType.NoOnlinePayments);
          return this.reportError(new Error("Couldn't go to checkout due to volatileCartService error"));
        });
    } else {
      try {
        this.nextProps({toastErrorMessage: ''});
        this.volatileCart = await this.checkoutApiService.createCartV2(params);
      } catch (error) {
        const toastErrorMessage = this.errorHandler.getResolvedError(error)?.message;
        this.nextProps({toastErrorMessage});
        return {checkoutId: undefined};
      }
    }

    return {checkoutId: this.checkoutId};
  };

  public resetCart() {
    this.volatileCart = null;
  }

  public get checkoutId() {
    return this.volatileCart?.checkoutId;
  }
}
